import React, { Component, useState } from 'react';
import { istTime } from '../lib/ist';
import { AlertTriangle, RefreshCw, Wifi, WifiOff, Camera, Printer, CreditCard, ChevronDown, ChevronUp, Copy, CheckCircle2 } from 'lucide-react';
import { ERROR_TYPES } from '../utils/errorHandling';

/**
 * Error Boundary Component - Catches React errors
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Log to backend if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      const techDetails = this.state.error
        ? `${this.state.error.toString()}\n\n${this.state.errorInfo?.componentStack || ''}`
        : null;
      return (
        <ErrorDisplay
          title="Something Went Wrong"
          message="The booth ran into an unexpected error."
          action="Tap the button below to restart this screen. The booth software keeps running."
          onRetry={this.handleReset}
          retryText="Restart Screen"
          type={ERROR_TYPES.UNKNOWN}
          code="REACT_CRASH"
          technicalDetails={techDetails}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Error Display Component - Shows user-friendly error messages.
 * Now supports an error code badge, copy-to-clipboard, and a collapsible
 * "Show technical details" panel for the operator. The customer sees the
 * friendly message; the operator can expand the details when reporting an
 * issue to support so we get the real stack trace / response body.
 */
export function ErrorDisplay({
  title = 'Error',
  message = 'Something went wrong.',
  action = 'Please try again.',
  onRetry,
  retryText = 'Try Again',
  type = ERROR_TYPES.UNKNOWN,
  showIcon = true,
  fullScreen = true,
  countdown = null,
  onCancel,
  cancelText = 'Cancel',
  code,
  technicalDetails,
}) {
  const [showTech, setShowTech] = useState(false);
  const [copied, setCopied] = useState(false);

  const getIcon = () => {
    switch (type) {
      case ERROR_TYPES.CAMERA:
        return <Camera className="w-16 h-16 text-rose-400" />;
      case ERROR_TYPES.NETWORK:
        return <WifiOff className="w-16 h-16 text-amber-400" />;
      case ERROR_TYPES.PRINT:
        return <Printer className="w-16 h-16 text-blue-400" />;
      case ERROR_TYPES.PAYMENT:
        return <CreditCard className="w-16 h-16 text-purple-400" />;
      default:
        return <AlertTriangle className="w-16 h-16 text-rose-400" />;
    }
  };

  const handleCopy = async () => {
    try {
      const blob = [
        `Error: ${title}`,
        code ? `Code: ${code}` : '',
        `Message: ${message}`,
        `Time: ${new Date().toISOString()}`,
        technicalDetails ? `\n--- Technical details ---\n${technicalDetails}` : '',
      ].filter(Boolean).join('\n');
      await navigator.clipboard.writeText(blob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard may be denied */ }
  };

  const content = (
    <div className="text-center p-8 max-w-md mx-auto">
      {showIcon && (
        <div className="flex justify-center mb-6">
          {getIcon()}
        </div>
      )}

      <h2 className="text-2xl font-bold text-white mb-3" data-testid="error-title">
        {title}
      </h2>

      {code && (
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-wider text-white/40 font-mono px-2 py-0.5 rounded bg-white/10" data-testid="error-code">
            code: {code}
          </span>
          <button
            onClick={handleCopy}
            className="text-[10px] text-white/40 hover:text-white/80 flex items-center gap-1"
            data-testid="error-copy-btn"
          >
            {copied ? <><CheckCircle2 className="w-3 h-3" /> copied</> : <><Copy className="w-3 h-3" /> copy</>}
          </button>
        </div>
      )}

      <p className="text-white/80 text-lg mb-2" data-testid="error-message">
        {message}
      </p>

      <p className="text-white/60 text-sm mb-8 whitespace-pre-line">
        {action}
      </p>

      {countdown !== null && (
        <p className="text-white/50 text-sm mb-4">
          Auto-retry in {countdown} seconds...
        </p>
      )}

      <div className="flex flex-col gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 px-8 rounded-xl text-lg font-medium transition-colors"
            data-testid="error-retry-btn"
          >
            <RefreshCw className="w-5 h-5" />
            {retryText}
          </button>
        )}

        {onCancel && (
          <button
            onClick={onCancel}
            className="w-full bg-white/10 hover:bg-white/20 text-white py-3 px-8 rounded-xl text-base transition-colors"
            data-testid="error-cancel-btn"
          >
            {cancelText}
          </button>
        )}
      </div>

      {technicalDetails && (
        <div className="mt-6 text-left">
          <button
            onClick={() => setShowTech((v) => !v)}
            className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1 mx-auto"
            data-testid="error-tech-toggle"
          >
            {showTech ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showTech ? 'Hide technical details' : 'Show technical details'}
          </button>
          {showTech && (
            <pre className="mt-3 text-[11px] font-mono text-white/60 bg-black/40 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap break-words" data-testid="error-tech-details">
              {technicalDetails}
            </pre>
          )}
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-charcoal-900 flex items-center justify-center z-50" data-testid="error-display">
        {content}
      </div>
    );
  }

  return (
    <div className="bg-charcoal-800/90 rounded-2xl" data-testid="error-display">
      {content}
    </div>
  );
}

/**
 * Network Status Banner - Shows when offline
 */
export function NetworkStatusBanner({ isOnline, onRetry }) {
  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-3 px-4 flex items-center justify-center gap-3 z-50 animate-pulse">
      <WifiOff className="w-5 h-5" />
      <span className="font-medium">No Internet Connection</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm"
        >
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Loading Overlay with optional error
 */
export function LoadingOverlay({
  message = 'Loading...',
  error = null,
  onRetry,
  onCancel
}) {
  if (error) {
    return (
      <ErrorDisplay
        title={error.title}
        message={error.message}
        action={error.action}
        type={error.type}
        onRetry={onRetry}
        onCancel={onCancel}
        fullScreen={true}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-charcoal-900/95 flex flex-col items-center justify-center z-50">
      <div className="w-16 h-16 border-4 border-rose-500/30 border-t-rose-500 rounded-full animate-spin mb-6" />
      <p className="text-white text-xl">{message}</p>
    </div>
  );
}

/**
 * Session Timeout Warning Modal
 */
export function SessionTimeoutWarning({ 
  remainingSeconds, 
  onContinue, 
  onTimeout 
}) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-charcoal-800 rounded-2xl p-8 max-w-md mx-4 text-center">
        <div className="text-6xl font-bold text-amber-400 mb-4">
          {remainingSeconds}
        </div>
        
        <h2 className="text-xl font-bold text-white mb-2">
          Are you still there?
        </h2>
        
        <p className="text-white/70 mb-6">
          Your session will reset in {remainingSeconds} seconds.
        </p>
        
        <button
          onClick={onContinue}
          className="w-full bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-xl text-lg font-medium transition-colors"
          data-testid="session-continue-btn"
        >
          Yes, I'm here!
        </button>
      </div>
    </div>
  );
}

/**
 * ====================================================================
 * Operator Error Console
 * --------------------------------------------------------------------
 * A subtle, persistent UI that lets the venue operator see ALL errors
 * the booth has hit during the current session — without taking the
 * customer-facing flow off track.
 *
 * UX contract:
 *   - A small, faded "i" icon in the bottom-right corner. Easy to find
 *     for staff, easy for customers to ignore.
 *   - Tap to open a slide-up panel listing the most recent errors with
 *     timestamp, code, message, and an expandable JSON details view.
 *   - Includes "Clear" and "Copy all" so staff can WhatsApp logs to support.
 *   - When the most recent entry is fresh (<3s old), the icon turns
 *     red + pulses so staff notice failures even mid-session.
 *
 * Wire-up:
 *   <ErrorConsole errors={errors} onClear={() => setErrors([])} />
 * where `errors` is an array of {ts, code, title, message, type, data}
 * ====================================================================
 */
export function ErrorConsole({ errors = [], onClear }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedTs, setExpandedTs] = useState(null);

  const errorCount = errors.length;
  const newest = errors[0];
  const newestIsFresh = newest && (Date.now() - newest.ts < 3000);

  const handleCopy = async () => {
    try {
      const blob = errors.map((e) => {
        const lines = [
          `[${new Date(e.ts).toISOString()}] ${e.title || 'Error'}`,
          e.code ? `  code: ${e.code}` : '',
          e.message ? `  msg: ${e.message}` : '',
          e.data ? `  data: ${JSON.stringify(e.data)}` : '',
        ].filter(Boolean);
        return lines.join('\n');
      }).join('\n\n');
      await navigator.clipboard.writeText(blob || '(no errors)');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard denied */ }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-3 right-3 z-40 w-9 h-9 rounded-full flex items-center justify-center transition-all shadow-lg ${
          newestIsFresh
            ? 'bg-rose-500 text-white animate-pulse'
            : errorCount > 0
              ? 'bg-amber-500/80 text-white hover:bg-amber-500'
              : 'bg-white/10 text-white/40 hover:bg-white/20 hover:text-white/70'
        }`}
        title={errorCount > 0 ? `${errorCount} recent error${errorCount === 1 ? '' : 's'}` : 'No errors'}
        data-testid="error-console-toggle"
      >
        <AlertTriangle className="w-4 h-4" />
        {errorCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-white text-rose-600 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {errorCount > 9 ? '9+' : errorCount}
          </span>
        )}
      </button>

      {/* Slide-up panel */}
      {open && (
        <div className="fixed bottom-14 right-3 z-40 w-96 max-w-[calc(100vw-1.5rem)] max-h-[60vh] bg-charcoal-900 border border-white/10 rounded-xl shadow-2xl flex flex-col" data-testid="error-console-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <div className="text-white text-sm font-medium">Operator Error Console</div>
              <div className="text-white/40 text-[11px]">{errorCount} recent {errorCount === 1 ? 'event' : 'events'}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopy}
                className="text-[11px] text-white/60 hover:text-white px-2 py-1 rounded hover:bg-white/10 flex items-center gap-1"
                data-testid="error-console-copy"
              >
                {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy all</>}
              </button>
              <button
                onClick={() => { onClear?.(); setExpandedTs(null); }}
                className="text-[11px] text-white/60 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                data-testid="error-console-clear"
              >
                Clear
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10"
              >
                ×
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {errorCount === 0 ? (
              <div className="text-center text-white/40 text-xs py-12">
                No errors recorded this session.
              </div>
            ) : (
              <ul className="space-y-1">
                {errors.map((e) => {
                  const isOpen = expandedTs === e.ts;
                  const hasData = e.data || e.technicalDetails;
                  const levelColor = {
                    error: 'border-rose-500/40 bg-rose-500/5',
                    warn: 'border-amber-500/40 bg-amber-500/5',
                    info: 'border-blue-500/40 bg-blue-500/5',
                  }[e.level || 'error'] || 'border-rose-500/40 bg-rose-500/5';
                  return (
                    <li key={e.ts} className={`border-l-2 ${levelColor} rounded-r px-3 py-2`}>
                      <button
                        onClick={() => hasData && setExpandedTs(isOpen ? null : e.ts)}
                        className="w-full text-left"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-mono text-white/40 whitespace-nowrap">
                            {istTime(e.ts)}
                          </span>
                          {e.code && (
                            <span className="text-[10px] font-mono text-white/60 px-1.5 py-0.5 rounded bg-white/10">
                              {e.code}
                            </span>
                          )}
                          <span className="text-xs text-white font-medium">{e.title || 'Error'}</span>
                        </div>
                        {e.message && (
                          <div className="text-xs text-white/70 mt-1 break-words">{e.message}</div>
                        )}
                      </button>
                      {isOpen && hasData && (
                        <pre className="mt-2 text-[10px] font-mono text-white/50 bg-black/40 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-words">
                          {e.data ? JSON.stringify(e.data, null, 2) : e.technicalDetails}
                        </pre>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Hook: collect a rolling buffer of errors for the ErrorConsole.
 * Usage:
 *   const { errors, pushError, clearErrors } = useErrorBuffer();
 *   ...
 *   pushError({ code: 'PRINT_FAILED', title: 'Print Failed', message, data: result });
 */
export function useErrorBuffer(maxEntries = 50) {
  const [errors, setErrors] = useState([]);

  const pushError = (entry) => {
    setErrors((prev) => {
      const next = [{ ts: Date.now(), level: 'error', ...entry }, ...prev];
      return next.slice(0, maxEntries);
    });
  };

  const clearErrors = () => setErrors([]);

  return { errors, pushError, clearErrors };
}

export default ErrorBoundary;